 
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useRef } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import FormSelect from '../../components/forms/form-select';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import SourceViewDialog from '../pipelines/source-view-dialog';
import { pipelinesService } from '../../services/pipelines.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { knowledgeBaseService } from '../../services/knowledge-base.service';
import { userLookupService } from '../../services/user-lookup.service';
import { EmptyState, Metric, PageSkeleton, Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import { PANEL, PRETTY, riseAt } from '../../components/common/ui-kit';
import type { KnowledgeBaseDocument } from '../../types';
import { formatBytes, formatDate, sourceKindLabel, statusTone } from './knowledge-base-status';

const DOC_PAGE_SIZE = 50;

const KnowledgeBaseView = () => {
  const { id, isReady, navigateToEdit } = useSecureNavigation({ entity: 'knowledge-base', basePath: '/knowledge-bases' });
  const toast = useRef<Toast>(null);
  const canEdit = useCanUpdate(JOB_NAMES.KNOWLEDGE_BASE);

  // `?tab=documents` is how the create form hands over: a knowledge base that has
  // just been made has nothing to show but the place to put its documents.
  const [searchParams] = useSearchParams();
  const TAB_INDEX: Record<string, number> = { overview: 0, documents: 1, pipelines: 2, audit: 3 };
  const [tab, setTab] = useState(TAB_INDEX[searchParams.get('tab') || ''] ?? 0);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [docPage, setDocPage] = useState(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [upload, setUpload] = useState<{ done: number; total: number; failed: string[] } | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState<'TEXT' | 'URL'>('TEXT');
  const [addTitle, setAddTitle] = useState('');
  const [addBody, setAddBody] = useState('');
  const [viewId, setViewId] = useState<string | null>(null);
  const [removeDoc, setRemoveDoc] = useState<KnowledgeBaseDocument | null>(null);
  const [linkPipelineId, setLinkPipelineId] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['knowledge-bases', id],
    queryFn: () => knowledgeBaseService.getById(id!),
    enabled: isReady && !!id,
  });
  useBreadcrumbTitle(data?.name);

  // Paged: a corpus can hold tens of thousands of documents, and asking for "all"
  // once shipped 15,402 of them to this tab in one response.
  const { data: docs, isLoading: docsLoading, isError: docsError } = useQuery({
    queryKey: ['kb-documents', id, includeSuperseded, docPage],
    queryFn: () => knowledgeBaseService.documents(id!, {
      include_superseded: includeSuperseded, page: docPage, page_size: DOC_PAGE_SIZE,
    }),
    enabled: isReady && !!id,
    placeholderData: (previous) => previous,
  });

  const { data: pipelines = [] } = useQuery({
    queryKey: ['kb-pipelines', id],
    queryFn: () => knowledgeBaseService.pipelines(id!),
    enabled: isReady && !!id,
  });

  const { data: people } = useQuery({
    queryKey: ['kb-people', data?.createdBy, data?.updatedBy],
    queryFn: () => userLookupService.getNamesByIds([data?.createdBy, data?.updatedBy]),
    enabled: !!data,
  });
  const who = (ref?: string | null) => (ref && people?.get(ref)) || '—';

  const refreshCorpus = () => {
    queryClient.invalidateQueries({ queryKey: ['kb-documents', id] });
    queryClient.invalidateQueries({ queryKey: ['knowledge-bases', id] });
    queryClient.invalidateQueries({ queryKey: ['kb-pipelines', id] });
  };
  const toastError = (m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 10000 });
  const toastOk = (m: string) => toast.current?.show({ severity: 'success', summary: 'Done', detail: m, life: 4000 });

  /** Several files at once, one request each so one bad file does not sink the rest. */
  const onFilesPicked = async (files: FileList | null) => {
    if (!files?.length || !id) return;
    const list = Array.from(files);
    const failed: string[] = [];
    setUpload({ done: 0, total: list.length, failed });
    for (const f of list) {
      try {
        await knowledgeBaseService.uploadDocument(id, f);
      } catch (e) {
        failed.push(`${f.name}: ${extractErrorMessage(e, 'upload failed')}`);
      }
      setUpload({ done: list.indexOf(f) + 1, total: list.length, failed: [...failed] });
    }
    if (fileInput.current) fileInput.current.value = '';
    refreshCorpus();
    const ok = list.length - failed.length;
    if (ok) toastOk(`${ok} document${ok === 1 ? '' : 's'} added.`);
    if (failed.length) toastError(failed.join('\n'));
    setTimeout(() => setUpload(null), 1500);
  };

  const addMutation = useMutation({
    mutationFn: () => knowledgeBaseService.addDocument(id!, addKind === 'TEXT'
      ? { sourceType: 'TEXT', text: addBody, title: addTitle || undefined }
      : { sourceType: 'URL', url: addBody.trim(), title: addTitle || undefined }),
    onSuccess: () => {
      toastOk(addKind === 'TEXT' ? 'Text added.' : 'URL added — it is fetched when a pipeline next runs.');
      setAddOpen(false); setAddTitle(''); setAddBody(''); refreshCorpus();
    },
    onError: (e) => toastError(extractErrorMessage(e, 'Could not add it')),
  });

  const removeMutation = useMutation({
    mutationFn: (docId: string) => knowledgeBaseService.removeDocument(id!, docId),
    onSuccess: () => { toastOk('Document removed.'); setRemoveDoc(null); refreshCorpus(); },
    onError: (e) => { toastError(extractErrorMessage(e, 'Could not remove it')); setRemoveDoc(null); },
  });

  // Pipelines that could be pointed at this corpus: live ones that do not read a KB yet.
  const { data: allPipelines } = useQuery({
    queryKey: ['pipelines', 'linkable'],
    queryFn: () => pipelinesService.list({ page: 1, page_size: 200 }),
    enabled: isReady && !!id && tab === 2,
  });
  const linkable = ((allPipelines?.data || []) as any[])
    .filter((p) => !p.kbRef && !p.isDelete)
    .map((p) => ({ value: p._id, label: p.name ? `${p.name} (${p.pipelineCode})` : p.pipelineCode }));

  const linkMutation = useMutation({
    mutationFn: () => pipelinesService.update(linkPipelineId, { kbRef: id } as any),
    onSuccess: () => {
      toastOk('Linked. Documents that pipeline had uploaded now belong to this knowledge base.');
      setLinkPipelineId('');
      queryClient.invalidateQueries({ queryKey: ['pipelines'] });
      refreshCorpus();
    },
    onError: (e) => toastError(extractErrorMessage(e, 'Could not link the pipeline')),
  });

  if (isLoading || !data) return <PageSkeleton panels={3} />;

  const card = `${PANEL} p-4`;
  const rows = docs?.data || [];
  const docTotal: number = docs?.pagination?.totalItems ?? rows.length;
  const docPages: number = docs?.pagination?.totalPages ?? 1;
  const firstShown = docTotal ? (docPage - 1) * DOC_PAGE_SIZE + 1 : 0;

  return (
    <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900" data-testid="SCR-KnowledgeBase-View">
      <Toast ref={toast} position="top-right" />
      <input ref={fileInput} type="file" multiple className="hidden"
             accept=".pdf,.docx,.txt,.md,.markdown,.html,.htm,.csv,.json"
             onChange={(e) => onFilesPicked(e.target.files)} data-testid="KnowledgeBaseView.File" />
      <ScreenHeader
        icon="pi pi-book"
        onBack={() => window.history.back()}
        title={data.name}
        right={
          <>
            <span className="font-mono text-xs">{data.kbCode}</span>
            {canEdit && (
              <>
                <span className="ui-press inline-flex">
                  <Button type="button" label="Upload documents" icon="pi pi-upload" size="small"
                          onClick={() => { setTab(1); fileInput.current?.click(); }}
                          disabled={!!upload} data-testid="KnowledgeBaseView.Upload" />
                </span>
                <span className="ui-press inline-flex">
                  <Button type="button" label="Edit" icon="pi pi-pencil" size="small" severity="secondary" outlined
                          onClick={() => navigateToEdit(id!)} />
                </span>
              </>
            )}
          </>
        }
      />

      {/* At-rest summary: what this corpus is, how big it is, and when a pipeline
          last read it. The questions someone opening this page actually has. */}
      <Rise className={`${card} mb-4`} i={0}>
        <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
          <Metric label="Documents" value={String(data.documentCount ?? 0)} />
          <Metric label="Size" value={formatBytes(data.totalBytes)} mono />
          <Metric label="Made of" value={sourceKindLabel(data.sourceKind)} />
          <Metric label="Last indexed" value={formatDate(data.lastIngestedAt)} />
          <div>
            <span className="block text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">
              Status
            </span>
            <span className="mt-1 block">
              <StatusChip tone={statusTone(data.status)}>
                {data.status === 'ACTIVE' ? 'Active' : 'Inactive'}
              </StatusChip>
            </span>
          </div>
        </div>
      </Rise>

      <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
        {/* ── Overview ───────────────────────────────────────────────── */}
        <TabPanel header="Overview">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className={card}>
              <SectionLabel icon="pi pi-file" className="mb-2.5">Content</SectionLabel>
              <dl className="flex flex-col gap-2 text-sm">
                <Row label="Made of" value={sourceKindLabel(data.sourceKind)} />
                <Row label="Suggested chunking" value={data.suggestedChunkingPreset || 'no suggestion'} />
                <Row label="Tags" value={data.tags?.length ? data.tags.join(' · ') : '—'} />
                {data.description && <Row label="Description" value={data.description} />}
              </dl>
              <p className="mt-3 text-xs leading-relaxed text-ink-muted" style={PRETTY}>
                The suggested preset is a starting point the pipeline form fills in. Chunking
                belongs to the pipeline, which is the thing that can be re-run — and one corpus
                indexed two ways is the point of keeping them separate.
              </p>
            </div>

            <div className={card}>
              <SectionLabel icon="pi pi-info-circle" className="mb-2.5">Record</SectionLabel>
              <dl className="flex flex-col gap-2 text-sm">
                <Row label="Code" value={<span className="font-mono text-xs">{data.kbCode}</span>} />
                <Row label="Created" value={`${formatDate(data.createdAt)} by ${who(data.createdBy)}`} />
                <Row label="Updated" value={data.updatedAt ? `${formatDate(data.updatedAt)} by ${who(data.updatedBy)}` : '—'} />
                <Row label="Last indexed" value={formatDate(data.lastIngestedAt)} />
              </dl>
            </div>
          </div>
        </TabPanel>

        {/* ── Documents ──────────────────────────────────────────────── */}
        {/* The count is the corpus's, from the summary — not the rows on this page. */}
        <TabPanel header={`Documents (${(data.documentCount ?? 0).toLocaleString()})`}>
          <div className={card}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <SectionLabel icon="pi pi-folder-open">
                {includeSuperseded ? 'Current and superseded' : 'Current documents'}
              </SectionLabel>
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <InputSwitch checked={includeSuperseded}
                               onChange={(e) => { setIncludeSuperseded(!!e.value); setDocPage(1); }} />
                  Show superseded versions
                </label>
                {canEdit && (
                  <>
                    <span className="ui-press inline-flex">
                      <Button type="button" label="Add text / URL" icon="pi pi-plus" size="small" severity="secondary" outlined
                              onClick={() => setAddOpen(true)} data-testid="KnowledgeBaseView.AddText" />
                    </span>
                    <span className="ui-press inline-flex">
                      <Button type="button" label={upload ? `Uploading ${upload.done}/${upload.total}…` : 'Upload files'}
                              icon={upload ? 'pi pi-spin pi-spinner' : 'pi pi-upload'} size="small"
                              disabled={!!upload} onClick={() => fileInput.current?.click()} />
                    </span>
                  </>
                )}
              </div>
            </div>
            {/* What happens next. A document added here is not searchable until a
                pipeline that reads this corpus runs — say so, and say how. */}
            {canEdit && pipelines.length === 0 && (data.documentCount ?? 0) > 0 && (
              <div className="ui-callout mb-3 px-3 py-2 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
                These documents are stored but not searchable yet. A <strong>pipeline</strong> chunks and indexes them —
                {' '}<button type="button" className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                              onClick={() => setTab(2)}>create or link one on the Pipelines tab</button>.
              </div>
            )}
            {/* Loading and failure each say so. Rendering the empty state for both
                told a reader a 15,402-document corpus was empty. */}
            {docsLoading ? (
              <PageSkeleton panels={1} className="" />
            ) : docsError ? (
              <EmptyState icon="pi pi-exclamation-triangle" title="Could not load the documents"
                          hint="The knowledge base itself loaded; try the tab again in a moment." />
            ) : rows.length === 0 ? (
              <EmptyState icon="pi pi-file-import" title="No documents in this corpus yet"
                          hint="Upload PDFs, Word, text, Markdown, HTML, CSV or JSON — several at once — or add pasted text or a web page."
                          action={canEdit ? (
                            <span className="ui-press inline-flex">
                              <Button type="button" label="Upload files" icon="pi pi-upload" size="small"
                                      onClick={() => fileInput.current?.click()} disabled={!!upload} />
                            </span>
                          ) : undefined} />
            ) : (
              <>
                <div className="flex flex-col gap-2">
                  {rows.map((doc, i) => (
                    <DocumentRow key={doc._id} doc={doc} index={i}
                                 onView={() => setViewId(doc._id)}
                                 onRemove={canEdit && doc.isCurrent ? () => setRemoveDoc(doc) : undefined} />
                  ))}
                </div>
                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-ink-muted">
                  <span className="tabular-nums" data-testid="KnowledgeBaseView.Documents.Range">
                    {firstShown.toLocaleString()}–{(firstShown + rows.length - 1).toLocaleString()} of {docTotal.toLocaleString()}
                  </span>
                  <span className="flex gap-2">
                    <Button label="Previous" size="small" outlined severity="secondary"
                            disabled={docPage <= 1} onClick={() => setDocPage((p) => p - 1)} />
                    <Button label="Next" size="small" outlined severity="secondary"
                            disabled={docPage >= docPages} onClick={() => setDocPage((p) => p + 1)} />
                  </span>
                </div>
              </>
            )}
          </div>
        </TabPanel>

        {/* ── Pipelines ──────────────────────────────────────────────── */}
        <TabPanel header={`Pipelines${pipelines.length ? ` (${pipelines.length})` : ''}`}>
          <div className={card}>
            <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
              <SectionLabel icon="pi pi-sitemap">Recipes that index this corpus</SectionLabel>
              {canEdit && (
                <span className="ui-press inline-flex">
                  <Button type="button" label="Create a pipeline for this corpus" icon="pi pi-plus" size="small"
                          onClick={() => navigate(`/pipelines/create?kb=${encodeURIComponent(id!)}`)}
                          data-testid="KnowledgeBaseView.CreatePipeline" />
                </span>
              )}
            </div>
            {pipelines.length === 0 ? (
              <EmptyState icon="pi pi-sitemap" title="No pipeline reads this knowledge base yet"
                          hint="A pipeline is what chunks and indexes these documents so a RAG can search them. Create one here, or link a pipeline you already have. Several pipelines may read one corpus — to index it different ways." />
            ) : (
              <div className="flex flex-col gap-2">
                {pipelines.map((p, i) => (
                  <div key={p._id} className="ui-inset ui-rise flex items-center justify-between gap-3 px-3 py-2.5"
                       style={riseAt(i)}>
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">{p.name || p.pipelineCode}</div>
                      <div className="truncate font-mono text-[11px] text-ink-muted">{p.pipelineCode}</div>
                    </div>
                    <StatusChip tone={statusTone(p.status)}>{p.status === 'ACTIVE' ? 'Active' : 'Inactive'}</StatusChip>
                  </div>
                ))}
              </div>
            )}
            {/* An existing pipeline that owns its own documents can be pointed here
                instead; the backend moves the documents it uploaded into this corpus. */}
            {canEdit && (
              <div className="mt-4 border-t border-gray-100 pt-4 dark:border-gray-700">
                <SectionLabel icon="pi pi-link" className="mb-2">Link a pipeline you already have</SectionLabel>
                {linkable.length === 0 ? (
                  <p className="text-xs text-ink-muted">Every pipeline already reads a knowledge base.</p>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="min-w-[18rem] flex-1">
                      <FormSelect label="Pipeline" name="linkPipeline" value={linkPipelineId} options={linkable} filter
                                  onChange={(e: any) => setLinkPipelineId(e?.target?.value ?? '')}
                                  helperText="Its own uploaded documents move into this knowledge base." />
                    </div>
                    <span className="ui-press inline-flex mb-5">
                      <Button type="button" label={linkMutation.isPending ? 'Linking…' : 'Link'} icon="pi pi-link" size="small"
                              disabled={!linkPipelineId || linkMutation.isPending} onClick={() => linkMutation.mutate()}
                              data-testid="KnowledgeBaseView.LinkPipeline" />
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabPanel>

        {/* ── Audit ──────────────────────────────────────────────────── */}
        <TabPanel header="Audit">
          <div className={card}>
            <SectionLabel icon="pi pi-history" className="mb-2.5">Audit trail</SectionLabel>
            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300" style={PRETTY}>
              Every create, update and delete on this knowledge base is written to the platform
              log with the actor, their IP, and — for an update — the list of fields that
              actually changed.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300" style={PRETTY}>
              Open <strong>Logs → Log Transactions</strong> and filter on
              {' '}<code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[11px] dark:bg-gray-700">KNOWLEDGEBASE.*</code>{' '}
              or on this entity id.
            </p>
            <dl className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3 text-sm dark:border-gray-700">
              <Row label="Entity id" value={<span className="font-mono text-xs">{data._id}</span>} />
              <Row label="Created" value={`${formatDate(data.createdAt)} by ${who(data.createdBy)}`} />
              <Row label="Last updated" value={data.updatedAt ? `${formatDate(data.updatedAt)} by ${who(data.updatedBy)}` : '—'} />
            </dl>
          </div>
        </TabPanel>
      </TabView>

      <Dialog header="Add to this knowledge base" visible={addOpen} onHide={() => setAddOpen(false)}
              style={{ width: '520px', maxWidth: '95vw' }} modal>
        <div className="flex flex-col gap-3">
          <FormSelect label="Kind" name="kind" value={addKind}
                      options={[{ label: 'Pasted text', value: 'TEXT' }, { label: 'Web page (URL)', value: 'URL' }]}
                      onChange={(e: any) => setAddKind((e?.target?.value ?? 'TEXT') as 'TEXT' | 'URL')} />
          <Input label="Title" value={addTitle} onChange={(v: string) => setAddTitle(v)} placeholder="Optional" />
          {addKind === 'TEXT'
            ? <TextArea label="Text" value={addBody} onChange={(v: string) => setAddBody(v)} rows={8} placeholder="Paste the content to index" />
            : <Input label="URL" value={addBody} onChange={(v: string) => setAddBody(v)} placeholder="https://…" />}
          <div className="flex justify-end gap-2">
            <span className="ui-press inline-flex"><Button type="button" label="Cancel" severity="secondary" text onClick={() => setAddOpen(false)} /></span>
            <span className="ui-press inline-flex">
              <Button type="button" label={addMutation.isPending ? 'Adding…' : 'Add'} icon="pi pi-plus"
                      disabled={addMutation.isPending || !addBody.trim()} onClick={() => addMutation.mutate()} />
            </span>
          </div>
        </div>
      </Dialog>

      <SourceViewDialog kbId={id!} sourceId={viewId} onHide={() => setViewId(null)} />

      <DeleteConfirmDialog visible={!!removeDoc} onCancel={() => setRemoveDoc(null)}
                           onDelete={() => removeDoc && removeMutation.mutate(removeDoc._id)}
                           message={`Remove “${removeDoc?.title || removeDoc?.fileName || 'this document'}” from the corpus? Indexes already built keep it until each pipeline's next run.`}
                           testId="KnowledgeBaseView.Dialog.Remove" loading={removeMutation.isPending}
                           header="Remove document" confirmLabel="Remove" loadingLabel="Removing..." />
    </div>
  );
};

/** A label/value pair on one baseline, so a column of them lines up. */
const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="flex-none text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">
      {label}
    </dt>
    <dd className="min-w-0 text-right text-ink" style={PRETTY}>{value}</dd>
  </div>
);

/** One document. A superseded version is dimmed and shows the window it was true
 *  for, which is the thing the version chain exists to answer. */
const DocumentRow = ({ doc, index, onView, onRemove }: {
  doc: KnowledgeBaseDocument; index: number; onView: () => void; onRemove?: () => void;
}) => (
  <div className={`ui-inset ui-rise flex items-start justify-between gap-3 px-3 py-2.5 ${doc.isCurrent ? '' : 'opacity-60'}`}
       style={riseAt(index)}>
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <span className="truncate text-sm text-ink">
          {doc.title || doc.fileName || doc.url || doc._id}
        </span>
        <span className="flex-none text-[11px] tabular-nums text-ink-muted">v{doc.version}</span>
        {!doc.isCurrent && <StatusChip tone="neutral" dot={false}>superseded</StatusChip>}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-ink-muted">
        <span>{doc.sourceType}</span>
        {doc.fileType && <span>· {doc.fileType}</span>}
        {!!doc.fileSize && <span>· {formatBytes(doc.fileSize)}</span>}
        {!!doc.imageCount && <span>· {doc.imageCount} image{doc.imageCount === 1 ? '' : 's'}</span>}
        {/* URL provenance: a citation to a page that has changed is worse than none,
            so what was fetched and when is shown, not just the link. */}
        {doc.siteName && <span>· {doc.siteName}</span>}
        {doc.fetchedAt && <span>· fetched {formatDate(doc.fetchedAt)}</span>}
        {doc.httpStatus && doc.httpStatus >= 400 && (
          <span className="ui-ink ui-ink--bad">· HTTP {doc.httpStatus}</span>
        )}
      </div>
      {doc.validationError && (
        <div className="ui-ink ui-ink--bad mt-1 text-[11px]">{doc.validationError}</div>
      )}
    </div>
    <div className="flex flex-none items-center gap-2">
      <span className="text-right text-[11px] tabular-nums text-ink-muted">
        {doc.isCurrent
          ? <>from {formatDate(doc.effectiveFrom)}</>
          : <>{formatDate(doc.effectiveFrom)} → {formatDate(doc.effectiveTo)}</>}
      </span>
      <button type="button" onClick={onView} aria-label="View document" title="View document"
              className="ui-press flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              data-testid="KnowledgeBaseView.ViewDocument">
        <i className="pi pi-eye ui-act--view" aria-hidden />
      </button>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove document" title="Remove document"
                className="ui-press flex h-9 w-9 items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                data-testid="KnowledgeBaseView.RemoveDocument">
          <i className="pi pi-trash ui-act--delete" aria-hidden />
        </button>
      )}
    </div>
  </div>
);

export default KnowledgeBaseView;
